import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Mail, Phone, MapPin } from 'lucide-react';
import SEO from '../components/SEO';
import { PRIVACY_CONTACT, CONSENT_VERSION } from '../utils/dpdp';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">{title}</h2>
    <div className="text-slate-700 leading-relaxed space-y-3 text-sm md:text-base">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  return (
    <div data-testid="privacy-policy-page">
      <SEO
        title="Privacy Policy | Rubicon Finservices"
        description="How Rubicon Finservices collects, uses, protects and shares your personal data in compliance with India's Digital Personal Data Protection Act, 2023."
        keywords="privacy policy, DPDP Act 2023, data protection, Rubicon Finservices"
      />
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck size={32} className="text-amber-300" />
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-blue-100 text-sm">Compliant with the Digital Personal Data Protection Act, 2023 (India) · Version {CONSENT_VERSION}</p>
        </div>
      </section>

      <article className="py-14 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <p className="text-slate-600 mb-8 text-sm md:text-base">
            {PRIVACY_CONTACT.company} ({PRIVACY_CONTACT.arn}) ("we", "us") is committed to protecting your personal data.
            This policy explains what personal data we collect, why, how we protect it, and the rights you have as a
            Data Principal under the Digital Personal Data Protection Act, 2023 ("DPDP Act").
          </p>

          <Section title="1. Who we are (Data Fiduciary)">
            <p>{PRIVACY_CONTACT.company}, {PRIVACY_CONTACT.address}. We act as the Data Fiduciary for the personal data collected through this website and our onboarding forms.</p>
          </Section>

          <Section title="2. What personal data we collect">
            <ul className="list-disc pl-5 space-y-1">
              <li>Identity & contact details — name, email, phone, address.</li>
              <li>KYC information — PAN, Aadhaar, date of birth, bank details, nominee details.</li>
              <li>Risk profiling responses and financial preferences.</li>
              <li>For non-individual entities — entity details, authorised signatory details and documents.</li>
              <li>Documents you upload (ID proofs, photographs, bank proofs, entity documents).</li>
            </ul>
          </Section>

          <Section title="3. Why we collect it (Purpose)">
            <p>We process your personal data only for specified, lawful purposes: risk profiling, KYC and onboarding for mutual fund investments, providing investment recommendations, servicing your account, responding to your enquiries, and meeting our legal and regulatory obligations.</p>
          </Section>

          <Section title="4. Legal basis — Consent">
            <p>We collect and process your personal data based on your free, specific, informed, unconditional and unambiguous <strong>consent</strong>, given through a clear affirmative action at the point of collection. You may withdraw your consent at any time (see Section 7); withdrawal is as easy as giving consent and does not affect the lawfulness of processing done before withdrawal.</p>
          </Section>

          <Section title="5. How we share your data">
            <p>We share your data only as necessary with Asset Management Companies (AMCs), Registrar & Transfer Agents (RTAs), KYC Registration Agencies (KRAs) and regulatory authorities to execute and service your investments, and with secure service providers who process data on our behalf under confidentiality obligations. We do not sell your personal data.</p>
          </Section>

          <Section title="6. Data retention">
            <p>We retain your personal data only for as long as necessary to fulfil the stated purposes and to comply with applicable legal, regulatory and audit requirements. When the purpose is served and retention is no longer required by law, we erase or anonymise your data.</p>
          </Section>

          <Section title="7. Your rights as a Data Principal">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Right to access</strong> — obtain a summary of the personal data we process and with whom it has been shared.</li>
              <li><strong>Right to correction & erasure</strong> — request correction, completion, updating or erasure of your data.</li>
              <li><strong>Right to withdraw consent</strong> — as easily as it was given.</li>
              <li><strong>Right to nominate</strong> — nominate another person to exercise your rights in case of death or incapacity.</li>
              <li><strong>Right to grievance redressal</strong> — a readily available means to raise a complaint.</li>
            </ul>
            <p>
              To exercise any of these rights, please use our{' '}
              <Link to="/data-request" className="text-blue-600 underline font-semibold">Data Rights request form</Link>. We will respond within the timeframe prescribed under the DPDP Act (up to 90 days).
            </p>
          </Section>

          <Section title="8. Security safeguards">
            <p>We implement reasonable security safeguards to protect your personal data, including encryption in transit (HTTPS), access controls with role-based restrictions, secure object storage for uploaded documents, hashed credentials, and audit logging. In the event of a personal data breach, we will notify the Data Protection Board and affected Data Principals as required under the DPDP Act.</p>
          </Section>

          <Section title="9. Children's data">
            <p>For any Data Principal below 18 years of age, we process personal data only with verifiable consent of a parent or lawful guardian, and we do not undertake tracking or targeted advertising directed at children.</p>
          </Section>

          <Section title="10. Cookies & tracking">
            <p>This website uses only essential/functional browser storage required to operate the site (for example, keeping team members signed in). We do not use third-party tracking or advertising cookies.</p>
          </Section>

          <Section title="11. Grievance Officer">
            <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-5 space-y-2">
              <p className="font-semibold text-slate-900">{PRIVACY_CONTACT.grievanceOfficer}</p>
              <p className="flex items-center gap-2"><Mail size={16} className="text-amber-600" /> <a href={`mailto:${PRIVACY_CONTACT.grievanceEmail}`} className="text-blue-600 underline">{PRIVACY_CONTACT.grievanceEmail}</a></p>
              <p className="flex items-center gap-2"><Phone size={16} className="text-amber-600" /> <a href={`tel:${PRIVACY_CONTACT.phone.replace(/[^+0-9]/g, '')}`} className="text-blue-600 underline">{PRIVACY_CONTACT.phone}</a></p>
              <p className="flex items-start gap-2"><MapPin size={16} className="text-amber-600 mt-0.5" /> {PRIVACY_CONTACT.address}</p>
            </div>
            <p className="mt-3">If you are not satisfied with our response, you may lodge a complaint with the <strong>Data Protection Board of India</strong>.</p>
          </Section>

          <Section title="12. Changes to this policy">
            <p>We may update this policy from time to time. The current version and date are shown at the top of this page.</p>
          </Section>
        </div>
      </article>
    </div>
  );
};

export default PrivacyPolicy;
